import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import { Seo } from "../components/Seo";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Globe } from "lucide-react";

const ARCH_IMG = "https://images.unsplash.com/photo-1596716147725-bc96cc16147d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdHxlbnwwfHx8fDE3Nzg0MzY3NDl8MA&ixlib=rb-4.1.0&q=85";
const EASE = [0.22, 1, 0.36, 1];

const PROOF_POINTS = [
  { Icon: Sparkles, text: "AI-polished prose, never AI-fabricated facts" },
  { Icon: ShieldCheck, text: "ATS-aware across Workday, Greenhouse, Lever" },
  { Icon: Globe, text: "Optimised for 140+ countries' CV conventions" },
];

const Signup = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = (params.get("ref") || "").trim().toUpperCase();
    if (code) {
      localStorage.setItem("cv_ref", code);
      setRefCode(code);
    } else {
      setRefCode((localStorage.getItem("cv_ref") || "").toUpperCase());
    }
  }, [location.search]);

  const nextPath = new URLSearchParams(location.search).get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, refCode || undefined);
      const partnerKey = localStorage.getItem("cv_partner_key");
      if (partnerKey) {
        try { await api.post(`/partners/${partnerKey}/track-signup`); } catch { /* non-fatal */ }
        localStorage.removeItem("cv_partner_key");
        localStorage.removeItem("cv_partner_name");
      }
      localStorage.removeItem("cv_ref");
      if (refCode) toast.success(`Account created. 50 bonus credits added — thanks ${refCode}.`);
      else toast.success("Account created.");
      navigate(nextPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen surface-dark">
      <Seo
        title="Create your free account | PrestiqueResume"
        description="Sign up free for PrestiqueResume — build a recruiter-ready CV in minutes. No credit card required."
        path="/signup"
      />
      <Header variant="dark" />

      <div className="grid lg:grid-cols-[1fr_1.05fr] min-h-[calc(100vh-72px)]">
        {/* LEFT — form */}
        <div className="relative flex items-center justify-center px-6 sm:px-10 py-16 lg:py-0 order-2 lg:order-1">
          <div className="absolute inset-0 spotlight-glow opacity-60 pointer-events-none" />
          <motion.form
            onSubmit={submit}
            data-testid="signup-form"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="relative w-full max-w-md"
          >
            <div className="eyebrow-gold mb-5">Begin · Member · First edition</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.04] mb-4 text-warm-ivory">
              Begin your<br />
              <span className="editorial-accent">first edition.</span>
            </h1>
            <p className="text-warm-ivory/65 mb-9 text-base leading-relaxed max-w-sm">
              Free to start. Two hundred editorial templates. Pay only when you export.
            </p>

            {refCode && (
              <div
                data-testid="signup-ref-banner"
                className="mb-7 rounded-lg border border-burnished-gold/30 bg-burnished-gold/[0.08] px-4 py-3 text-sm font-mono"
              >
                <span className="uppercase tracking-widest text-[10px] text-burnished-gold">Invited by</span>{" "}
                <strong className="text-warm-ivory font-medium">{refCode}</strong>
                <span className="block text-[11px] mt-1 text-warm-ivory/55">
                  +50 bonus credits applied at sign-up.
                </span>
              </div>
            )}

            <GoogleAuthButton testid="signup-google-btn" label="Sign up with Google" />
            <div className="my-7 flex items-center gap-4 text-warm-ivory/35">
              <hr className="flex-1 hairline-dark" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">or sign up with email</span>
              <hr className="flex-1 hairline-dark" />
            </div>

            <div className="space-y-7">
              <div>
                <label className="eyebrow-gold block mb-3">Full name</label>
                <input
                  type="text" required value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name" autoComplete="name"
                  className="w-full bg-transparent border-0 border-b border-warm-ivory/20 pb-2 text-warm-ivory placeholder-warm-ivory/30 focus:outline-none focus:border-burnished-gold transition-colors text-base"
                />
              </div>
              <div>
                <label className="eyebrow-gold block mb-3">Email</label>
                <input
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="signup-email" autoComplete="email"
                  className="w-full bg-transparent border-0 border-b border-warm-ivory/20 pb-2 text-warm-ivory placeholder-warm-ivory/30 focus:outline-none focus:border-burnished-gold transition-colors text-base"
                />
              </div>
              <div>
                <label className="eyebrow-gold block mb-3">Password (≥ 6 characters)</label>
                <input
                  type="password" required minLength={6} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="signup-password" autoComplete="new-password"
                  className="w-full bg-transparent border-0 border-b border-warm-ivory/20 pb-2 text-warm-ivory placeholder-warm-ivory/30 focus:outline-none focus:border-burnished-gold transition-colors text-base"
                />
              </div>
              <Button
                disabled={loading}
                data-testid="signup-submit"
                className="btn-gold w-full h-12 rounded-full font-mono uppercase tracking-[0.20em] text-[11px]"
              >
                {loading ? "Creating…" : "Create account"}
              </Button>
            </div>

            <div className="mt-8 text-sm">
              <span className="text-warm-ivory/55">Already a subscriber? </span>
              <Link to="/login" className="text-burnished-gold hover:text-warm-ivory underline underline-offset-4 decoration-burnished-gold/30 hover:decoration-warm-ivory transition-colors">
                Sign in
              </Link>
            </div>
          </motion.form>
        </div>

        {/* RIGHT — cinematic editorial photograph + proof points */}
        <div className="relative hidden lg:block overflow-hidden bg-matte-black order-1 lg:order-2">
          <img src={ARCH_IMG} alt="Editorial architecture" className="absolute inset-0 w-full h-full object-cover opacity-65" />
          <div className="absolute inset-0 bg-gradient-to-tl from-matte-black via-matte-black/55 to-transparent" />
          <div className="absolute inset-0 bg-deep-navy/30 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-matte-black/85" />
          <div className="grain-dark absolute inset-0" />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
            className="relative h-full flex flex-col justify-end p-14 xl:p-20"
          >
            <div className="eyebrow-gold mb-6">Hiring manager · overheard</div>
            <div className="font-display text-3xl xl:text-[2.6rem] leading-[1.12] tracking-[-0.015em] text-warm-ivory max-w-md mb-12">
              "Make me read <span className="editorial-accent">past line one</span>."
            </div>

            <div className="space-y-3.5 max-w-md">
              {PROOF_POINTS.map((p, i) => (
                <motion.div
                  key={p.text}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + i * 0.12, duration: 0.6, ease: EASE }}
                  className="flex items-start gap-3"
                >
                  <p.Icon className="w-4 h-4 text-burnished-gold mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-warm-ivory/75 text-sm leading-snug">{p.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <Footer variant="dark" />
    </div>
  );
};

export default Signup;
